<template>
  <div>
    <v-layout
      v-if="componentLoading"
      row
      wrap
      justify-center
    >
      <v-progress-circular
        :size="64"
        :width="3"
        indeterminate
        color="primary"
      />
    </v-layout>
    <v-layout
      v-else
      row
      wrap
    >
      <v-flex xs12>
        <v-card
          id="visitorTypeContent"
          class="max-shadow"
        >
          <v-toolbar
            color="white"
            flat
          >
            <v-toolbar-title class="blue-grey--text text--darken-3">
              Visitor Types
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                color="primary"
                flat
                @click.stop="addVisitorTypeDialog = true"
              >
                Add
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <div
            id="visitorTypes"
          >
            <v-layout
              column
              wrap
              align-center
            >
              <v-layout
                v-for="visitorType in sortedVisitorTypes"
                :key="visitorType.id"
                row
                wrap
                align-center
                justify-space-around
                class="visitorTypes"
              >
                <span
                  style="flex-grow:1"
                  class="subheading font-weight-medium px-3 visitorTypeTitle blue-grey--text text--darken-3"
                >
                  {{ visitorType.general.type_name }}
                </span>
                <v-layout
                  row
                  align-center
                  wrap
                  style="max-width:300px; min-width: 300px; flex-grow:1"
                >
                  <Toggle
                    :id="`t_${visitorType.id}`"
                    v-model="visitorType.general.enabled"
                    :disabled="shouldDisable(visitorType)"
                    class="visitorTypeToggle"
                    @change="updateToggle(visitorType, $event)"
                  />
                  <v-spacer />
                  <div class="visitorTypeActions">
                    <v-btn
                      slot="activator"
                      icon
                      flat
                      @click.stop="$router.push(`/${role}/settings/vms/checkinflow/${visitorType.id}`)"
                    >
                      <v-icon color="iconColor">
                        edit
                      </v-icon>
                    </v-btn>
                    <template v-if="shouldShowDelete(visitorType.id)">
                      <!-- FIXME: there's issue with tooltip here.
                    if we leave cursor in the same place even after navigating to different component, tooltip persists and will not close -->
                      <v-btn
                        slot="activator"
                        :disabled="shouldDisable(visitorType)"
                        icon
                        flat
                        @click.stop="deleteVisitorType(visitorType)"
                      >
                        <v-icon color="iconColor">
                          delete
                        </v-icon>
                      </v-btn>
                    </template>
                  </div>
                </v-layout>
              </v-layout>
            </v-layout>
          </div>
        </v-card>
      </v-flex>
      <v-dialog
        v-model="deleteVisitorTypeDialog"
        lazy
        max-width="600px"
      >
        <v-card>
          <v-card-title primary-title>
            <div class="headline">
              Delete {{ selectedVisitorType.general.type_name }}
            </div>
          </v-card-title>
          <v-card-text>
            <div class="subheading">
              Do you want to delete
              <strong>{{ selectedVisitorType.general.type_name }}</strong>?
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              flat
              color="primary"
              @click.stop="deleteVisitorTypeDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              flat
              color="primary"
              @click.stop="confirmDeleteVisitorType(selectedVisitorType)"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="addVisitorTypeDialog"
        max-width="600px"
      >
        <v-card class="max-shadow">
          <v-card-title primary-title>
            <div class="headline">
              Add Visitor Type
            </div>
          </v-card-title>
          <v-card-text>
            <v-layout
              column
              align-center
            >
              <div
                v-if="visitorTypes.length > 0"
                style="width: 400px"
              >
                <v-form
                  ref="form"
                >
                  <v-text-field
                    v-model="newVisitorTypeName"
                    :rules="[v => !!v && v.length > 3 || 'minimum 4 characters']"
                    label="Visitor Type Name"
                    required
                  />
                  <v-select
                    v-model="copySettingsFrom"
                    :items="visitorTypes"
                    label="Copy Settings From"
                    item-text="general.type_name"
                  />
                </v-form>
              </div>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              flat
              @click.stop="addVisitorTypeDialog = false, $refs.form.reset()"
            >
              cancel
            </v-btn>
            <v-btn
              color="primary"
              flat
              @click.stop="addVisitorType"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-snackbar
        v-model="snackbar"
        :timeout="0"
        :color="snackbarColor"
        absolute
        multi-line
        bottom
      >
        {{ snackbarMessage }}
        <v-btn
          flat
          @click.native="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>
    </v-layout>
  </div>
</template>
<script>
import _get from 'lodash/get';
import _set from 'lodash/set';
import _omit from 'lodash/omit';
import _cloneDeep from 'lodash/cloneDeep';
import { database } from '@/firebase';
import role from '@/mixins/roles';
import Toggle from '@/components/common/Toggle';

export default {
  name: 'VisitorTypes',
  components: {
    Toggle
  },
  mixins: [role],
  data() {
    return {
      sortedVisitorTypes: [],
      visitorTypes: [],
      firebaseListeners: [],
      addVisitorTypeDialog: false,

      selectedVisitorType: {
        general: {}
      },
      newVisitorTypeName: '',
      copySettingsFrom: '',

      visitorTypeTemplate: {
        general: {
          index: 1,
          type_name: '',
          enabled: true
        },
        phone_screen: {
          otp_verification: true
        },
        host_screen: {
          display_text: 'Please select the person you want to meet',
          host_selection: true
        },
        checkin_fields: [
          {
            id: 'full_name',
            label: 'Full Name',
            required: true,
            type: 'text_field',
            input_type: 'text'
          },
          {
            id: 'email',
            label: 'Email Address',
            required: false,
            type: 'text_field',
            input_type: 'email',
            hide: false
          },
          {
            id: 'company',
            label: 'Company',
            required: false,
            type: 'text_field',
            input_type: 'text',
            hide: false
          }
        ],
        agreement: {
          visitor_agreement: true,
          /* eslint-disable max-len */
          append_content:
            '<!DOCTYPE html><html style="zoom: 0.54" lang="en"><head><title></title><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="padding: 18px">{{editorContent}}<br><br><div id="visitor-sign" style="{{display}}"><p style="text-align: center"><img src="{{sign}}" height="72"></p><hr style="margin: 12px"><p style="text-align: center;font-size: medium">{{name}}</p><p style="text-align: center">{{date}}</p></div></body></html>',
          content:
            '<!DOCTYPE html><html style="zoom: 0.54" lang="en"><head><title></title><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="padding: 18px"><h2 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0); text-align: center;"><span style="font-weight: 700;"><span class="ql-cursor"><br class="Apple-interchange-newline">﻿﻿﻿</span>WELCOME TO OUR WORKPLACE&nbsp;</span></h2><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><h3 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0);"><span style="font-weight: 700;"><u>VISITOR GUIDELINES</u></span></h3><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">&nbsp;&nbsp;&nbsp;&nbsp;Visitors are welcome to visit during hours of operations. For your safety &amp; security we have the following guidelines.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(a)&nbsp;&nbsp;Agree to follow the Division’s rules before entry is permitted into the building.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(b)&nbsp;&nbsp;All visitors must sign in and out through the main entrance lobby.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(c)&nbsp;&nbsp;All visitors are required to read and acknowledge the Non-Disclosure and Waiver Agreement.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(d)&nbsp;&nbsp;Smoking/tobacco use is prohibited in our facility. Please use designated outside areas.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(e)&nbsp;&nbsp;Firearms/weapons are prohibited in our facility.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(f)&nbsp;&nbsp;In the event of an emergency. Follow signage to the designated muster point.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><h3 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0);"><span style="font-weight: 700;"><u>VISITOR’S NON-DISCLOSURE AND WAIVER AGREEMENT</u></span></h3><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">&nbsp;&nbsp;&nbsp;&nbsp;During my visit to your facility, I will learn and/or have disclosed to me proprietary or confidential information (including, without limitations, information relating to technology, trade secrets, processes, materials, equipment, drawings, specifications, prototypes and products) and may receive samples of products which not generally known to the public (hereinafter collectively called “Confidential Information”).</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">In consideration of your permission to visit your facility &amp; for the courtesies extended to me during my visit:<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">1.&nbsp;&nbsp;I agree that I will not, without your written permission or that of your authorized representative, either;<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(a)&nbsp;&nbsp;Disclose or otherwise make available to others any Confidential Information disclosed to me during this and any subsequent visit which (i) was not known to me or my organization prior to disclosure by you, or (ii) is not now or subsequently becomes a part of the public domain as a result of publication or otherwise; or<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(b)&nbsp;&nbsp;Use or assist others in using or further developing in any manner any confidential information.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(c)&nbsp;&nbsp;Use cameras or video technology to disclose confidential information.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">2.&nbsp;&nbsp;I also agree to conform to any applicable safety requirements, which are brought to my attention by any employee or by signs posted in the areas that I visit while on the premises, and to observe other reasonable safety precautions.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">3.&nbsp;&nbsp;I further agree to release the Division, its officers, agents, employees, invitees or licensees from all claims, losses, expenses (including attorney’s fees), interest, damage and liability to the extent caused by or resulting from my negligence or willful misconduct.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">4.&nbsp;&nbsp;Please be advised by signing into the facility, you have acknowledged and understand the Visitor Guidelines and Non-Disclosure Agreement posted.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><span style="font-weight: 700;">THANK YOU</span></p><br><br><div id="visitor-sign" style="{{display}}"><p style="text-align: center"><img src="{{sign}}" height="72"></p><hr style="margin: 12px"><p style="text-align: center;font-size: medium">{{name}}</p><p style="text-align: center">{{date}}</p></div></body></html>',
          default_content:
            '<h2 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0); text-align: center;"><span style="font-weight: 700;"><span class="ql-cursor"><br class="Apple-interchange-newline">﻿﻿﻿</span>WELCOME TO OUR WORKPLACE&nbsp;</span></h2><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><h3 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0);"><span style="font-weight: 700;"><u>VISITOR GUIDELINES</u></span></h3><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">&nbsp;&nbsp;&nbsp;&nbsp;Visitors are welcome to visit during hours of operations. For your safety &amp; security we have the following guidelines.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(a)&nbsp;&nbsp;Agree to follow the Division’s rules before entry is permitted into the building.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(b)&nbsp;&nbsp;All visitors must sign in and out through the main entrance lobby.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(c)&nbsp;&nbsp;All visitors are required to read and acknowledge the Non-Disclosure and Waiver Agreement.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(d)&nbsp;&nbsp;Smoking/tobacco use is prohibited in our facility. Please use designated outside areas.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(e)&nbsp;&nbsp;Firearms/weapons are prohibited in our facility.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(f)&nbsp;&nbsp;In the event of an emergency. Follow signage to the designated muster point.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><h3 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0);"><span style="font-weight: 700;"><u>VISITOR’S NON-DISCLOSURE AND WAIVER AGREEMENT</u></span></h3><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">&nbsp;&nbsp;&nbsp;&nbsp;During my visit to your facility, I will learn and/or have disclosed to me proprietary or confidential information (including, without limitations, information relating to technology, trade secrets, processes, materials, equipment, drawings, specifications, prototypes and products) and may receive samples of products which not generally known to the public (hereinafter collectively called “Confidential Information”).</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">In consideration of your permission to visit your facility &amp; for the courtesies extended to me during my visit:<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">1.&nbsp;&nbsp;I agree that I will not, without your written permission or that of your authorized representative, either;<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(a)&nbsp;&nbsp;Disclose or otherwise make available to others any Confidential Information disclosed to me during this and any subsequent visit which (i) was not known to me or my organization prior to disclosure by you, or (ii) is not now or subsequently becomes a part of the public domain as a result of publication or otherwise; or<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(b)&nbsp;&nbsp;Use or assist others in using or further developing in any manner any confidential information.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(c)&nbsp;&nbsp;Use cameras or video technology to disclose confidential information.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">2.&nbsp;&nbsp;I also agree to conform to any applicable safety requirements, which are brought to my attention by any employee or by signs posted in the areas that I visit while on the premises, and to observe other reasonable safety precautions.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">3.&nbsp;&nbsp;I further agree to release the Division, its officers, agents, employees, invitees or licensees from all claims, losses, expenses (including attorney’s fees), interest, damage and liability to the extent caused by or resulting from my negligence or willful misconduct.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">4.&nbsp;&nbsp;Please be advised by signing into the facility, you have acknowledged and understand the Visitor Guidelines and Non-Disclosure Agreement posted.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><span style="font-weight: 700;">THANK YOU</span></p>',
          editor_content:
            '<h2 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0); text-align: center;"><span style="font-weight: 700;"><span class="ql-cursor"><br class="Apple-interchange-newline">﻿﻿﻿</span>WELCOME TO OUR WORKPLACE&nbsp;</span></h2><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><h3 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0);"><span style="font-weight: 700;"><u>VISITOR GUIDELINES</u></span></h3><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">&nbsp;&nbsp;&nbsp;&nbsp;Visitors are welcome to visit during hours of operations. For your safety &amp; security we have the following guidelines.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(a)&nbsp;&nbsp;Agree to follow the Division’s rules before entry is permitted into the building.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(b)&nbsp;&nbsp;All visitors must sign in and out through the main entrance lobby.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(c)&nbsp;&nbsp;All visitors are required to read and acknowledge the Non-Disclosure and Waiver Agreement.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(d)&nbsp;&nbsp;Smoking/tobacco use is prohibited in our facility. Please use designated outside areas.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(e)&nbsp;&nbsp;Firearms/weapons are prohibited in our facility.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(f)&nbsp;&nbsp;In the event of an emergency. Follow signage to the designated muster point.</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><h3 style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif; color: rgb(0, 0, 0);"><span style="font-weight: 700;"><u>VISITOR’S NON-DISCLOSURE AND WAIVER AGREEMENT</u></span></h3><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">&nbsp;&nbsp;&nbsp;&nbsp;During my visit to your facility, I will learn and/or have disclosed to me proprietary or confidential information (including, without limitations, information relating to technology, trade secrets, processes, materials, equipment, drawings, specifications, prototypes and products) and may receive samples of products which not generally known to the public (hereinafter collectively called “Confidential Information”).</p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">In consideration of your permission to visit your facility &amp; for the courtesies extended to me during my visit:<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">1.&nbsp;&nbsp;I agree that I will not, without your written permission or that of your authorized representative, either;<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(a)&nbsp;&nbsp;Disclose or otherwise make available to others any Confidential Information disclosed to me during this and any subsequent visit which (i) was not known to me or my organization prior to disclosure by you, or (ii) is not now or subsequently becomes a part of the public domain as a result of publication or otherwise; or<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(b)&nbsp;&nbsp;Use or assist others in using or further developing in any manner any confidential information.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">(c)&nbsp;&nbsp;Use cameras or video technology to disclose confidential information.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">2.&nbsp;&nbsp;I also agree to conform to any applicable safety requirements, which are brought to my attention by any employee or by signs posted in the areas that I visit while on the premises, and to observe other reasonable safety precautions.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">3.&nbsp;&nbsp;I further agree to release the Division, its officers, agents, employees, invitees or licensees from all claims, losses, expenses (including attorney’s fees), interest, damage and liability to the extent caused by or resulting from my negligence or willful misconduct.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;">4.&nbsp;&nbsp;Please be advised by signing into the facility, you have acknowledged and understand the Visitor Guidelines and Non-Disclosure Agreement posted.<br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><br></p><p style="margin-left: 25px; font-family: Roboto, &quot;Helvetica Neue&quot;, Helvetica, Arial, sans-serif;"><span style="font-weight: 700;">THANK YOU</span></p>'
        },
        photo: {
          visitor_photo: true,
          returning_visitor_photo: false
        },
        badge: {
          print_badge: true
        },
        notifications: {
          host_notifications: true,
          visitor_notifications: false,
          visitor_sms_text:
            'Thank you for visiting our work place. {{hostName}} will contact you shortly.',
          forward_notifications: false,
          // firebase deletes empty objects and arrays, so id as placeholder
          forward_notifications_to: [{ id: '' }, { id: '' }, { id: '' }]
        }
      },

      deleteVisitorTypeDialog: false,
      snackbar: false,
      snackbarColor: 'primary',
      snackbarMessage: '',

      componentLoading: true
    };
  },
  computed: {
    locationId() {
      return _get(this.$store.state.VMS, 'lid');
    },
    checkinFlowRef() {
      const lid = _get(this.$store.state.VMS, 'lid');
      // return null if the location ID is not yet set
      if (!lid) return null;

      return database.ref(`vms/${lid}/settings/checkin_flow`);
    }
  },
  watch: {
    checkinFlowRef: {
      handler(val) {
        if (!val) return;

        this.firebaseListeners.forEach(listenerFunc => {
          val.off('value', listenerFunc);
        });

        this.fetchVisitorTypes();
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.firebaseListeners.forEach(listenerFunc => {
      this.checkinFlowRef.off('value', listenerFunc);
    });
  },
  methods: {
    fetchVisitorTypes() {
      const visitorTypeQuery = this.checkinFlowRef.on(
        'value',
        visitorTypesSnapshot => {
          if (visitorTypesSnapshot) {
            this.componentLoading = false;
            const visitorTypes = visitorTypesSnapshot.val();
            const visitorTypesArray = this._toArray(visitorTypes);
            const sortedVisitorTypes = visitorTypesArray.sort(
              (a, b) => a.general.index - b.general.index
            );
            this.visitorTypes = visitorTypesArray;
            this.sortedVisitorTypes = sortedVisitorTypes;
          }
        }
      );
      this.firebaseListeners.push(visitorTypeQuery);
    },
    _toArray(obj) {
      if (!obj) return [];
      return Object.keys(obj).map(key => {
        const item = obj[key];
        item.id = key;
        return item;
      });
    },
    arrayToObject(array) {
      return array.reduce((obj, item) => {
        const key = item.id;
        const value = _omit(item, ['id']);
        // eslint-disable-next-line
        obj[key] = value;
        return obj;
      }, {});
    },
    shouldDisable(type) {
      const visitorTypes = _cloneDeep(this.visitorTypes);
      const shouldDisable =
        visitorTypes.filter(visitorType => visitorType.general.enabled).length <
        2;
      return type.general.enabled && shouldDisable;
    },

    shouldShowDelete(typeName) {
      return typeName !== 'visitor';
    },

    updateToggle(visitorType, val) {
      this.checkinFlowRef
        .child(`${visitorType.id}`)
        .child('general')
        .update({ enabled: val })
        .catch(error => {
          this.snackbarColor = 'error';
          this.snackbarMessage = error;
          this.snackbar = true;
        });
    },

    addVisitorType() {
      if (this.$refs.form.validate()) {
        // create key to reference visitor type in visitor types object
        const id = this.newVisitorTypeName
          .replace(/[^a-zA-Z0-9]/g, '_')
          .toLowerCase();

        let typeExists = Boolean;
        this.visitorTypes.forEach(vt => {
          typeExists = vt.id === id;
        });
        if (typeExists) {
          this.snackbarMessage = 'Visitor type exists!';
          this.snackbar = true;
        } else {
          const copySettingsFrom = this.visitorTypes.filter(
            obj => obj.general.type_name === this.copySettingsFrom
          )[0];
          const newVisitorType = _cloneDeep(
            copySettingsFrom || this.visitorTypeTemplate
          );
          delete newVisitorType.id;
          newVisitorType.general.type_name = this.newVisitorTypeName;
          newVisitorType.general.index = this.visitorTypes.length + 1;

          this.checkinFlowRef
            .child(`${id}`)
            .set(newVisitorType)
            .catch(error => {
              this.snackbarMessage = error;
              this.snackbarColor = 'error';
              this.snackbar = true;
            });

          this.$refs.form.reset();
          this.addVisitorTypeDialog = false;

          this.$router.push({ path: `settings/vms/checkinflow/${id}` });
        }
      }
    },

    deleteVisitorType(visitorType) {
      this.selectedVisitorType = _cloneDeep(visitorType);
      this.deleteVisitorTypeDialog = true;
    },

    confirmDeleteVisitorType(selectedVisitorType) {
      this.deleteVisitorTypeDialog = false;
      const visitorTypes = _cloneDeep(this.visitorTypes);
      if (visitorTypes.length < 2) return;

      const key = selectedVisitorType.id;
      this.checkinFlowRef
        .child(`${key}`)
        .set(null)
        .catch(error => {
          this.snackbarMessage = error;
          this.snackbarColor = 'error';
          this.snackbar = true;
        });

      const selectedTypeIndex = selectedVisitorType.general.index;

      this.visitorTypes.forEach((type, index) => {
        const typeIndex = type.general.index;
        if (typeIndex > selectedTypeIndex) {
          const newIndex = typeIndex - 1;
          _set(this.visitorTypes[index], 'general.index', newIndex);
        }
      });

      const newVisitorTypes = _cloneDeep(this.visitorTypes);

      newVisitorTypes.forEach(visitorType => {
        const newVisitorType = _cloneDeep(visitorType);
        delete newVisitorType.id;
      });

      const visitorTypesToDB = this.arrayToObject(newVisitorTypes);

      this.checkinFlowRef.set(visitorTypesToDB).catch(error => {
        this.snackbarMessage = error;
        this.snackbarColor = 'error';
        this.snackbar = true;
      });

      // console.log(this.visitorTypes);
    }
  }
};
</script>
<style>
#visitorTypeContent {
  height: calc(100vh - var(--toolbar-height)) !important;
  overflow-y: scroll;
}

#visitorTypes {
  height: calc(100vh - 190px) !important;
  overflow-y: scroll;
}

#visitorTypeContent .visitorTypes {
  width: 700px;
}
#visitorTypeContent .visitorTypeTitle {
  display: flex;
  justify-content: center;
}
#visitorTypeContent .visitorTypeToggle {
  padding-top: 8px;
}
#visitorTypeContent .visitorTypeActions {
  min-width: 110px;
}
</style>

